import React from 'react';

/**
 * A reusable toggle switch component with optional labels
 * 
 * @param {boolean} checked - Whether the toggle is on or off
 * @param {function} onChange - Callback when toggle is clicked
 * @param {string} leftLabel - Optional label on the left side
 * @param {string} rightLabel - Optional label on the right side
 * @param {boolean} withContainer - Whether to wrap in a styled container (default: false)
 * @param {string} activeColor - Tailwind color class for active state (default: 'bg-orange-500')
 * @param {string} inactiveColor - Tailwind color class for inactive state (default: 'bg-gray-300')
 * @param {string} className - Additional classes for the toggle button
 * @param {boolean} disabled - Greys the switch out and ignores clicks (e.g. while saving)
 * @param {string} id, ariaLabel, ariaLabelledBy, ariaDescribedBy - Accessible name/description of the switch
 */
export const ToggleSwitch = ({ 
    checked, 
    onChange, 
    leftLabel, 
    rightLabel, 
    withContainer = false,
    activeColor = 'bg-orange-500',
    inactiveColor = 'bg-gray-300',
    className = '',
    disabled = false,
    id,
    ariaLabel,
    ariaLabelledBy,
    ariaDescribedBy
}) => {
    const switchProps = {
        type: "button",
        role: "switch",
        "aria-checked": !!checked,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        "aria-describedby": ariaDescribedBy,
        id,
        disabled,
        onClick: onChange,
    };

    const toggleButton = (
        <button
            {...switchProps}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 ease-in-out shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                checked ? activeColor : inactiveColor
            } ${className}`}
        >
            <span 
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-all duration-200 ease-in-out ${
                    checked ? 'translate-x-6' : 'translate-x-1'
                }`}
            />
        </button>
    );

    // Simple toggle without labels
    if (!leftLabel && !rightLabel) {
        return toggleButton;
    }

    // Toggle with labels
    const toggleWithLabels = (
        <div className="flex items-center justify-center gap-4">
            {leftLabel && (
                <span className={`font-medium transition-colors duration-200 ease-in-out ${!checked ? 'text-orange-600' : 'text-gray-400'}`}>
                    {leftLabel}
                </span>
            )}
            <button
                {...switchProps}
                className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed ${
                    checked ? activeColor : inactiveColor
                } ${className}`}
            >
                <span 
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-all duration-200 ease-in-out ${
                        checked ? 'translate-x-8' : 'translate-x-1'
                    }`}
                />
            </button>
            {rightLabel && (
                <span className={`font-medium transition-colors duration-200 ease-in-out ${checked ? 'text-orange-600' : 'text-gray-400'}`}>
                    {rightLabel}
                </span>
            )}
        </div>
    );

    // With container styling
    if (withContainer) {
        return (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                {toggleWithLabels}
            </div>
        );
    }

    return toggleWithLabels;
};

export default ToggleSwitch;
